import { api } from './api';
import config from '../config';

export interface BingSiteConfig {
  id: number;
  tenantId: number;
  siteUrl: string;
  status: 'ACTIVE' | 'TOKEN_EXPIRED' | 'DISCONNECTED';
  connectedAt: string;
  lastSyncAt?: string;
  tokenExpiresAt: string;
}

export interface BingQuotaStatus {
  used: number;
  total: number;
  resetAt: string;
}

export interface ConnectBingRequest {
  authorizationCode: string;
  siteUrl: string;
}

export const bingService = {
  /**
   * Initiate OAuth flow by redirecting to Microsoft Identity Platform
   */
  initiateOAuth: (redirectUri: string) => {
    const clientId = config.VITE_BING_CLIENT_ID;
    const scope = 'https://api.bing.microsoft.com/.default';
    const state = crypto.randomUUID(); // CSRF protection
    
    sessionStorage.setItem('bing_oauth_state', state);
    
    const authUrl = new URL('https://login.microsoftonline.com/common/oauth2/v2.0/authorize');
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('scope', scope);
    authUrl.searchParams.append('state', state);
    authUrl.searchParams.append('prompt', 'consent');
    
    window.location.href = authUrl.toString();
  },

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  handleOAuthCallback: async (authorizationCode: string, siteUrl: string): Promise<BingSiteConfig> => {
    const response = await api.post<BingSiteConfig>(
      `/integrations/bing/connect`,
      {
        authorizationCode,
        siteUrl,
      }
    );
    return response.data;
  },

  /**
   * Get all connected Bing sites for current tenant
   */
  getConnectedSites: async (): Promise<BingSiteConfig[]> => {
    const response = await api.get<BingSiteConfig[]>(
      `/integrations/bing/sites`
    );
    return response.data;
  },

  /**
   * Disconnect a Bing site
   */
  disconnectSite: async (siteId: number): Promise<void> => {
    await api.delete(`/integrations/bing/sites/${siteId}`);
  },

  /**
   * Get current Bing API quota status (shared across all tenants)
   */
  getQuotaStatus: async (): Promise<BingQuotaStatus> => {
    const response = await api.get<BingQuotaStatus>(
      `/integrations/bing/quota`
    );
    return response.data;
  },

  /**
   * Trigger manual sync for a Bing site
   */
  triggerSync: async (siteId: number): Promise<void> => {
    await api.post(`/integrations/bing/sites/${siteId}/sync`);
  },

  /**
   * Verify site ownership in Bing Webmaster Tools
   */
  verifySiteOwnership: async (siteUrl: string, accessToken: string): Promise<boolean> => {
    const response = await api.post<{ verified: boolean }>(
      `/integrations/bing/verify`,
      { siteUrl, accessToken }
    );
    return response.data.verified;
  },
};
